/**
 * Pagination — sélecteur de page pour les tables (recette du DS Cosmos,
 * `design-system/components/navigation/Pagination.jsx`).
 *
 * Contrôlé : `page` est 1-indexé et vient du parent, qui recharge sa page de
 * données dans `onChange`. Le composant ne connaît ni les données ni leur
 * source — il n'affiche que la fenêtre de pages autour de la page courante,
 * avec les extrémités toujours atteignables.
 *
 * Accessibilité (RGAA 4.1.2 / WCAG 2.2) : la barre est un <nav> nommé, chaque
 * bouton porte un libellé explicite (« Page 3 »), la page courante est marquée
 * `aria-current="page"`, et les flèches sont neutralisées (`aria-hidden`) —
 * leur nom vient de l'`aria-label` du bouton, pas du chevron.
 */
const PAGE_BASE =
  'min-w-[34px] h-[34px] inline-flex items-center justify-center px-2 rounded-md border '
  + 'text-sm font-semibold cursor-pointer transition-colors duration-150';
const PAGE_IDLE = 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50';
const PAGE_ARROW =
  'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 '
  + 'disabled:opacity-40 disabled:cursor-not-allowed';
const PAGE_ACTIVE = 'bg-iris-600 border-transparent text-white shadow-[var(--shadow-primary)]';

/**
 * Fenêtre de pages : 1, …, page-1, page, page+1, …, pageCount.
 * Les « … » sont des marqueurs, pas des pages — d'où la clé distincte au rendu.
 */
const pageWindow = (page, pageCount) => {
  const pages = [1];
  const lo = Math.max(2, page - 1);
  const hi = Math.min(pageCount - 1, page + 1);
  if (lo > 2) pages.push('…');
  for (let p = lo; p <= hi; p++) pages.push(p);
  if (hi < pageCount - 1) pages.push('…');
  if (pageCount > 1) pages.push(pageCount);
  return pages;
};

const Pagination = ({ page = 1, pageCount = 1, onChange, label = 'Pagination', className = '' }) => {
  // Une seule page (ou aucune) : la barre n'offre aucun choix, on ne l'affiche pas.
  if (pageCount <= 1) return null;

  const go = (p) => { if (p >= 1 && p <= pageCount && p !== page) onChange?.(p); };

  return (
    <nav className={`flex items-center gap-1.5 font-sans ${className}`} aria-label={label}>
      <button
        type="button"
        onClick={() => go(page - 1)}
        disabled={page <= 1}
        aria-label="Page précédente"
        className={`${PAGE_BASE} ${PAGE_ARROW}`}
      >
        <span aria-hidden="true">‹</span>
      </button>
      {pageWindow(page, pageCount).map((p, i) => (p === '…' ? (
        <span key={`gap-${i}`} className="min-w-[34px] text-center text-slate-400 select-none" aria-hidden="true">…</span>
      ) : (
        <button
          key={p}
          type="button"
          onClick={() => go(p)}
          aria-label={`Page ${p}`}
          aria-current={p === page ? 'page' : undefined}
          className={`${PAGE_BASE} ${p === page ? PAGE_ACTIVE : PAGE_IDLE}`}
        >
          {p}
        </button>
      )))}
      <button
        type="button"
        onClick={() => go(page + 1)}
        disabled={page >= pageCount}
        aria-label="Page suivante"
        className={`${PAGE_BASE} ${PAGE_ARROW}`}
      >
        <span aria-hidden="true">›</span>
      </button>
    </nav>
  );
};

export default Pagination;
